/**
 * Web Bluetooth thermal printing. The server composes ESC/POS bytes (see
 * backend/src/services/escpos/); this hook's only job is pairing, connecting
 * and pushing those bytes to the printer over GATT — nothing here talks to
 * the API except to read/save the `printers` list itself.
 *
 * Cheap thermal printers don't use a standard assigned Bluetooth service, so
 * pairing shows every nearby device (`acceptAllDevices`) and then tries a
 * short list of known OEM service/characteristic UUID pairs to find the
 * writable one. If none match, the printer can still be used by entering its
 * UUIDs by hand in Settings (from the printer's manual) — see
 * docs/project-plan.html §11 ("manual UUID entry for an unlisted model").
 */
import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const KNOWN_PROFILES = [
  // Common "18F0/2AF1" printer profile used by many cheap OEM thermal printers.
  { serviceUuid: '000018f0-0000-1000-8000-00805f9b34fb', characteristicUuid: '00002af1-0000-1000-8000-00805f9b34fb' },
  // ISSC/Microchip transparent-UART profile, the other common one.
  { serviceUuid: '49535343-fe7d-4ae5-8fa9-9fafd205e455', characteristicUuid: '49535343-8841-43f4-a8d4-ecbe34729bb3' },
];

export function isBluetoothSupported() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Tries the printer's own saved UUIDs first, then the known profiles. */
async function discoverCharacteristic(server, printer) {
  const candidates = [];
  if (printer?.service_uuid && printer?.characteristic_uuid) {
    candidates.push({ serviceUuid: printer.service_uuid, characteristicUuid: printer.characteristic_uuid });
  }
  candidates.push(...KNOWN_PROFILES);

  let lastError;
  for (const profile of candidates) {
    try {
      const service = await server.getPrimaryService(profile.serviceUuid);
      const characteristic = await service.getCharacteristic(profile.characteristicUuid);
      return { characteristic, ...profile };
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError ?? new Error('No writable Bluetooth characteristic found on this device.');
}

export function useBluetoothPrinter() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('idle'); // idle | connecting | printing | done | failed
  const [error, setError] = useState(null);
  const queueRef = useRef(Promise.resolve());
  const connectionCache = useRef(new Map()); // printer.id -> { device, characteristic }

  const printersQuery = useQuery({ queryKey: ['printers'], queryFn: () => api.get('/printers') });
  const printers = printersQuery.data ?? [];
  const defaultPrinter = printers.find((p) => p.is_default) ?? printers[0] ?? null;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['printers'] });
  const saveMutation = useMutation({ mutationFn: (body) => api.post('/printers', body), onSuccess: invalidate });
  const updateMutation = useMutation({ mutationFn: ({ id, ...body }) => api.patch(`/printers/${id}`, body), onSuccess: invalidate });
  const deleteMutation = useMutation({ mutationFn: (id) => api.delete(`/printers/${id}`), onSuccess: invalidate });

  /** Shows the device chooser, identifies which service/characteristic to write to, and saves it. */
  const pairNewPrinter = useCallback(
    async ({ name, paperWidth = '58' } = {}) => {
      if (!isBluetoothSupported()) {
        throw new Error('This browser does not support Web Bluetooth. Use Chrome on Android to pair a printer.');
      }
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: KNOWN_PROFILES.map((p) => p.serviceUuid),
      });
      const server = await device.gatt.connect();
      const { serviceUuid, characteristicUuid } = await discoverCharacteristic(server, null);
      device.gatt.disconnect();

      return saveMutation.mutateAsync({
        name: name || device.name || 'Thermal printer',
        paperWidth,
        deviceId: device.id,
        deviceName: device.name || '',
        serviceUuid,
        characteristicUuid,
        isDefault: printers.length === 0,
      });
    },
    [printers.length, saveMutation],
  );

  /** Reconnects to a saved printer — silently if the browser remembers permission, else a fresh chooser prompt. */
  const connect = useCallback(async (printer) => {
    if (!isBluetoothSupported()) throw new Error('Web Bluetooth is not supported in this browser.');

    const cached = connectionCache.current.get(printer.id);
    if (cached && cached.device.gatt.connected) return cached;

    const remembered = (await navigator.bluetooth.getDevices?.())?.find((d) => d.id === printer.device_id);
    const device =
      remembered ??
      (await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [...new Set([...KNOWN_PROFILES.map((p) => p.serviceUuid), printer.service_uuid].filter(Boolean))],
      }));

    const server = await device.gatt.connect();
    const { characteristic } = await discoverCharacteristic(server, printer);

    const entry = { device, characteristic };
    connectionCache.current.set(printer.id, entry);
    return entry;
  }, []);

  /** Chunked GATT write with the printer's configured delay; queued so overlapping prints never interleave. */
  const print = useCallback(
    (bytes, printer) => {
      const target = printer ?? defaultPrinter;
      const run = async () => {
        if (!target) {
          setStatus('failed');
          setError('No printer saved yet. Pair one in Settings.');
          throw new Error('No printer saved.');
        }
        setStatus('connecting');
        setError(null);
        try {
          const { characteristic } = await connect(target);
          setStatus('printing');
          const buf = new Uint8Array(bytes);
          const chunkSize = target.chunk_size || 180;
          const delay = target.chunk_delay_ms ?? 20;
          for (let i = 0; i < buf.length; i += chunkSize) {
            await characteristic.writeValueWithoutResponse(buf.slice(i, i + chunkSize));
            if (delay) await sleep(delay);
          }
          setStatus('done');
        } catch (err) {
          setStatus('failed');
          setError(err.message || 'Could not print.');
          throw err;
        }
      };
      const chained = queueRef.current.then(run, run);
      queueRef.current = chained.catch(() => {});
      return chained;
    },
    [connect, defaultPrinter],
  );

  return {
    status,
    error,
    printers,
    defaultPrinter,
    isSupported: isBluetoothSupported(),
    isLoading: printersQuery.isLoading,
    pairNewPrinter,
    isPairing: saveMutation.isPending,
    print,
    updatePrinter: (id, body) => updateMutation.mutateAsync({ id, ...body }),
    deletePrinter: deleteMutation.mutateAsync,
  };
}

export default useBluetoothPrinter;
